import { Card, CardContent } from '../../../primitives/card';
import { InputNumber } from '../../../primitives/input-number';
import { ToggleGroup, ToggleGroupItem } from '../../../primitives/toggle-group';
import { RepeatIcon } from '../../../svg/repeat-icon';
import { ComboboxModel, ComboboxSeparator } from '../../combobox/combobox-model';
import { LabelWithoutRef } from '../../label/label-without-ref';
import { SelectModel } from '../../select/select-model';
import { SwitchCard } from '../../switch/switch-card';
import {
  CUSTOM_FREQUENCY_OPTIONS,
  DAY_OF_MONTH_OPTIONS,
  MONTH_OPTIONS,
  ORDINAL_OPTIONS,
  ORDINAL_WEEKDAY_OPTIONS,
  REPEAT_PRESET_OPTIONS_MODEL,
  WEEKDAY_OPTIONS,
} from '../constants';
import { useRecurrenceEditor } from '../hooks/use-recurrence-editor';
import { OrdinalWeekdayRule, RepeatPreset } from '../types';
import { describeRepeatPreset } from '../utils/describe-schedule';

interface RecurrenceEditorProps {
  disabled?: boolean;
  /** `undefined` means "Nunca" (no recurrence, i.e. a `scheduled` value). */
  expression: string | undefined;
  id?: string;
  /** The resolved `startsAt` instant; drives the day/weekday/month used by every preset. */
  startDate: Date;
  onChange: (preset: RepeatPreset, expression: string | undefined) => void;
}

const REPEAT_PRESET_MODEL = REPEAT_PRESET_OPTIONS_MODEL.map((option) => {
  if (option.item === ComboboxSeparator) {
    return { ...option };
  } else {
    return {
      ...option,
      key: option.item.toString(),
    };
  }
});

const CUSTOM_FREQUENCY_MODEL = CUSTOM_FREQUENCY_OPTIONS.map((option) => ({
  value: option.value,
  item: option.label,
  key: option.value.concat(option.label),
}));

const ORDINAL_MODEL = ORDINAL_OPTIONS.map((option) => ({
  value: option.value,
  item: option.label,
}));

const ORDINAL_WEEKDAY_MODEL = ORDINAL_WEEKDAY_OPTIONS.map((option) => ({
  value: option.value,
  item: option.label,
}));

interface OrdinalRuleFieldsProps {
  disabled?: boolean;
  id?: string;
  value: OrdinalWeekdayRule;
  onChange: (value: OrdinalWeekdayRule) => void;
}

/** The "El {primer, segundo, ...} {domingo, lunes, ..., día, día entre semana, día fin de semana}" pair. */
function OrdinalRuleFields({ disabled, id, value, onChange }: OrdinalRuleFieldsProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <SelectModel
        className="max-w-[12rem] flex-1"
        disabled={disabled}
        id={id ? `${id}-ordinal` : undefined}
        model={ORDINAL_MODEL}
        onValueChange={(ordinal) =>
          onChange({ ...value, ordinal: ordinal as OrdinalWeekdayRule['ordinal'] })
        }
        value={value.ordinal}
      />
      <SelectModel
        className="max-w-[12rem] flex-1"
        disabled={disabled}
        id={id ? `${id}-weekday` : undefined}
        model={ORDINAL_WEEKDAY_MODEL}
        onValueChange={(weekday) =>
          onChange({ ...value, weekday: weekday as OrdinalWeekdayRule['weekday'] })
        }
        value={value.weekday}
      />
    </div>
  );
}

/** Guided builder for `recurrence.expression`, with a raw fallback for expressions it can't represent. */
export function RecurrenceEditor({
  disabled = false,
  expression,
  id,
  startDate,
  onChange,
}: RecurrenceEditorProps) {
  const { preset, custom, isAdvanced, setPreset, updateCustom } = useRecurrenceEditor({
    expression,
    startDate,
    onChange,
  });

  return (
    <div className="space-y-4">
      <div className="w-full space-y-1">
        <div className="flex items-center gap-2">
          <RepeatIcon className="mb-1.5 shrink-0 text-success" />
          <LabelWithoutRef className="pt-0 text-xs font-medium text-muted-foreground">
            Repetir
          </LabelWithoutRef>
        </div>
        <ComboboxModel
          disabled={disabled}
          id={id ? `${id}-preset` : undefined}
          model={REPEAT_PRESET_MODEL}
          value={preset}
          onSelect={(value) => setPreset(value as RepeatPreset)}
        />
      </div>

      {!isAdvanced && preset === 'custom' && (
        <Card className="border-dashed shadow-none" variant="outline">
          <CardContent className="space-y-4 p-4">
            <div className="flex items-center gap-2">
              <div className="flex-1 space-y-1">
                <LabelWithoutRef className="pt-0 text-xs font-medium text-muted-foreground">
                  Frecuencia
                </LabelWithoutRef>
                <ComboboxModel
                  disabled={disabled}
                  id={id ? `${id}-frequency` : undefined}
                  model={CUSTOM_FREQUENCY_MODEL}
                  value={custom.frequency}
                  onSelect={(frequency) =>
                    updateCustom({ frequency: frequency as (typeof custom)['frequency'] })
                  }
                />
              </div>
              {custom.frequency !== 'weekly' && (
                <div className="space-y-1">
                  <LabelWithoutRef className="pt-0 text-xs font-medium text-muted-foreground">
                    Cada
                  </LabelWithoutRef>
                  <InputNumber
                    className="w-[5rem]"
                    disabled={disabled}
                    id={id ? `${id}-interval` : undefined}
                    min={1}
                    onValueChange={(value) => updateCustom({ interval: value ?? 1 })}
                    value={custom.interval}
                  />
                </div>
              )}
            </div>

            {custom.frequency === 'weekly' && (
              <div className="space-y-1">
                <LabelWithoutRef className="pt-0 text-xs font-medium text-muted-foreground">
                  Días de la semana
                </LabelWithoutRef>
                <ToggleGroup
                  className="flex-wrap justify-start gap-1"
                  disabled={disabled}
                  onValueChange={(weekdays) => weekdays.length > 0 && updateCustom({ weekdays })}
                  type="multiple"
                  value={custom.weekdays}
                  variant="outline"
                >
                  {WEEKDAY_OPTIONS.map((weekday) => (
                    <ToggleGroupItem
                      className="size-9 rounded-full border p-0"
                      aria-label={weekday.fullLabel}
                      key={weekday.value}
                      value={weekday.value}
                    >
                      {weekday.label}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
            )}

            {custom.frequency === 'monthly' && (
              <div>
                <div className="border-t border-dashed pb-4" />
                <ToggleGroup
                  className="flex-wrap justify-start gap-2 pb-4"
                  disabled={disabled}
                  onValueChange={(mode) =>
                    mode &&
                    updateCustom({
                      monthly: { ...custom.monthly, mode: mode as 'days' | 'ordinal' },
                    })
                  }
                  type="single"
                  value={custom.monthly.mode}
                  variant="accent"
                >
                  <ToggleGroupItem
                    className="rounded-full border bg-transparent text-primary hover:enabled:bg-primary hover:enabled:text-primary-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                    value="days"
                  >
                    Días del mes
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    className="rounded-full border bg-transparent text-primary hover:enabled:bg-primary hover:enabled:text-primary-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                    value="ordinal"
                  >
                    El
                  </ToggleGroupItem>
                </ToggleGroup>

                {custom.monthly.mode === 'days' ? (
                  <ToggleGroup
                    className="flex-wrap justify-start gap-1"
                    disabled={disabled}
                    onValueChange={(days) =>
                      days.length > 0 &&
                      updateCustom({
                        monthly: { ...custom.monthly, daysOfMonth: days.map(Number) },
                      })
                    }
                    type="multiple"
                    value={custom.monthly.daysOfMonth.map(String)}
                    variant="outline"
                  >
                    {DAY_OF_MONTH_OPTIONS.map((day) => (
                      <ToggleGroupItem
                        className="size-9 rounded-full border p-0"
                        key={day}
                        value={String(day)}
                      >
                        {day}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                ) : (
                  <OrdinalRuleFields
                    disabled={disabled}
                    id={id ? `${id}-monthly` : undefined}
                    onChange={(ordinal) =>
                      updateCustom({ monthly: { ...custom.monthly, ordinal } })
                    }
                    value={custom.monthly.ordinal}
                  />
                )}
              </div>
            )}

            {custom.frequency === 'yearly' && (
              <div className="space-y-4">
                <div className="border-t border-dashed" />
                <div className="space-y-1">
                  <LabelWithoutRef className="pt-0 text-xs font-medium text-muted-foreground">
                    Meses
                  </LabelWithoutRef>
                  <ToggleGroup
                    className="flex-wrap justify-start gap-1"
                    disabled={disabled}
                    onValueChange={(months) =>
                      months.length > 0 &&
                      updateCustom({ yearly: { ...custom.yearly, months: months.map(Number) } })
                    }
                    type="multiple"
                    value={custom.yearly.months.map(String)}
                    variant="outline"
                  >
                    {MONTH_OPTIONS.map((month) => (
                      <ToggleGroupItem
                        className="rounded-full border"
                        key={month.value}
                        value={String(month.value)}
                      >
                        {month.label}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                </div>

                <div className="border-t border-dashed" />
                <SwitchCard
                  sizesx="sm"
                  className="rounded-full"
                  label="Días de la semana"
                  checked={custom.yearly.useOrdinalWeekday}
                  disabled={disabled}
                  id={id ? `${id}-use-ordinal-weekday` : undefined}
                  onCheckedChange={(useOrdinalWeekday) =>
                    updateCustom({ yearly: { ...custom.yearly, useOrdinalWeekday } })
                  }
                />

                {custom.yearly.useOrdinalWeekday && (
                  <OrdinalRuleFields
                    disabled={disabled}
                    id={id ? `${id}-yearly` : undefined}
                    onChange={(ordinal) => updateCustom({ yearly: { ...custom.yearly, ordinal } })}
                    value={custom.yearly.ordinal}
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {preset !== 'never' && !isAdvanced && (
        <p className="text-xs text-muted-foreground">
          {describeRepeatPreset(preset, custom, startDate)}
        </p>
      )}
    </div>
  );
}
